import { useApp, useBehavior, useInput, useInputPhone } from '@libs/hooks';
import { AssetsSvgMap, TWidget, TWidgetProps } from '@libs/types';
import { camelCaseToKebabCase, ConditionalRender, getFieldName } from '@libs/utils';
import clsx from 'clsx';
import React, { useMemo, useRef, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { SvgComponent } from '../../common/Svg/SvgComponent';
import { useGetTelegramPhoneNumber } from '../../hooks/useTelegramMiniApp';
import { TConsumerInputPhoneData, TConsumerItem, TInputPhoneMessages } from '../../types/data';
import { RowEnum } from '../../types/enums';
import { TInputOption, TListOption } from '../../types/options';
import { ButtonRow } from '../Buttons/ButtonRow';
import { ListSelect } from '../List/select/ListSelect';
import { FlagIcon } from '../UI/FlagIcon/FlagIcon';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';
import { Modal } from '../UI/Modal/Modal';
import { PopupComponent } from '../UI/Popup/PopupComponent';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './inputPhone.module.css';
import { convertItemsToListSelect } from './utils/inputPhone';
import { PHONE_CUSTOM_MASK } from './utils/masks';

const InputPhone = ({ widget }: TWidgetProps<TConsumerInputPhoneData, TInputOption>) => {
    const phoneRef = useRef<HTMLInputElement | null>(null);
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const messages = (data?.messages ?? {}) as TInputPhoneMessages;
    const { value, isFocused, isError, errorMessage, label, helperText, handleFocus, handleBlur } = useInput(widget);
    const { iso2, handleChangeInputPhone } = useInputPhone(widget);
    const [itemId, setItemId] = useState(data.items?.find((i) => i.countryIso === iso2)?.id ?? widget?.data?.value);
    const { isDisabled, isHidden } = useBehavior(widget);
    const { setModal, setPopup } = useApp();
    const hasItems = Array.isArray(data.items) && data.items.length > 0;

    useGetTelegramPhoneNumber(widget, handleChangeInputPhone);

    const widgetForListSelect = useMemo(() => {
        return {
            ...widget?.data,
            uid: `${widget.uid}-modal`,
            data: {
                ...widget?.data,
                value: itemId,
                items: convertItemsToListSelect(widget?.data?.items, iso2 as string),
            },
        } as unknown as TWidget<TConsumerInputPhoneData, TInputOption>;
    }, [widget, itemId, iso2]);

    const onSelectItem = (_wid: string, item: TConsumerItem) => {
        if (hasItems && item) {
            setModal(null);
            setItemId(item.id);
            handleChangeInputPhone?.(
                item.metaText ?? '',
                {
                    countryCode: item.icon ?? '',
                    regex: item.regex,
                    isChangeCountry: true,
                },
                undefined,
                item.metaText,
            );
            //@ts-expect-error
            // https://github.com/bl00mber/react-phone-input-2/issues/187#issuecomment-799988994
            phoneRef?.current?.numberInputRef?.focus?.();
        }
    };

    const modalContent = (
        <Modal isCanCloseModal={true} iconName={AssetsSvgMap.ic_arrow_back} onHandleClick={() => setModal(null)}>
            <ConditionalRender condition={!!messages.headerForSelectPhonePrefix}>
                <TextCommon
                    isWidget={false}
                    options={{ variant: 'header', margin: { top: 16, left: 24, right: 24, bottom: 32 } }}
                    content={messages.headerForSelectPhonePrefix}
                />
            </ConditionalRender>
            <ListSelect
                rowId={RowEnum.SELECTOR_REGULAR}
                widget={widgetForListSelect as unknown as TWidget<TConsumerInputPhoneData, TListOption>}
                onSelectItem={onSelectItem}
                isOpenInModal={true}
            />
        </Modal>
    );

    const onOpenCountryList = () => {
        if (hasItems && !isDisabled) {
            setModal(modalContent);
        }
    };
    const errorPopup = () => {
        const selectedItem = data.items?.find((i) => i.id === itemId) as TConsumerItem;
        const errorDescription = messages.errorMessageDescription
            //@ts-expect-error
            ?.replace('$phone', phoneRef?.current?.state?.formattedNumber ?? value)
            ?.replace('$countryName', selectedItem?.countryName ?? '');
        return (
            <PopupComponent
                header={<TextCommon isWidget={false} content={errorDescription} />}
                internalContent={[
                    <ButtonRow
                        key="button_ic_check"
                        as="button"
                        isWidget={false}
                        label={messages.buttonFirstLabelLabel}
                        iconName={data.buttonFirstLabelIcon ?? 'ic_arrow_retry'}
                        handleClick={() => {
                            setPopup(null);
                            onOpenCountryList();
                        }}
                    />,
                    <ButtonRow
                        key="button_ic_check"
                        as="button"
                        isWidget={false}
                        label={messages.buttonSecondLabelLabel}
                        iconName={data.buttonSecondLabelIcon ?? 'ic_pencil'}
                        handleClick={() => {
                            setPopup(null);
                            //@ts-expect-error
                            // https://github.com/bl00mber/react-phone-input-2/issues/187#issuecomment-799988994
                            phoneRef?.current?.numberInputRef?.focus?.();
                        }}
                    />,
                ]}
            />
        );
    };

    return (
        <InputWrapper
            dataTest={widget.uid}
            widget={widget}
            labelText={label}
            helperText={helperText}
            isError={isError}
            isShowErrorHintButton={isError && !!messages.errorMessageDescription}
            handleErrorClick={() => setPopup(errorPopup)}
            isDisabled={isDisabled}
            isHidden={isHidden}
            errorMessage={errorMessage}
            isFocused={isFocused}
            isFilled
            marginTop={options.margin?.top}
            marginRight={options.margin?.right}
            marginBottom={options.margin?.bottom}
            marginLeft={options.margin?.left}
            leftInputComponent={
                <div
                    data-test={`${widget.uid}-dropdown`}
                    className={clsx(styles.flagIconDropdown, isDisabled && styles.isDisabled)}
                    onClick={() => onOpenCountryList()}
                >
                    <FlagIcon name={iso2} />
                    <ConditionalRender condition={hasItems}>
                        <div className={styles.iconContainer}>
                            <SvgComponent fill="black" name={AssetsSvgMap.ic_chevron_down} />
                        </div>
                    </ConditionalRender>
                </div>
            }
        >
            <PhoneInput
                //@ts-expect-error
                ref={phoneRef}
                inputProps={{
                    autoFocus: options.onFocus || false,
                    name: getFieldName(options.autofill, 'tel'),
                    autoComplete: camelCaseToKebabCase(options.autofill ?? 'tel'),
                    inputMode: 'tel',
                    required: data.mandatory,
                    maxLength: options.maxLength,
                    readOnly: isDisabled,
                    tabIndex: data.tabIndex,
                }}
                value={value}
                specialLabel={''}
                placeholder={''}
                disableDropdown={true}
                countryCodeEditable={false}
                defaultMask={'... ... ... ... ..'}
                masks={PHONE_CUSTOM_MASK}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChangeInputPhone}
            />
        </InputWrapper>
    );
};
export { InputPhone };
